import StoreOrderStepBadge from '@/dealers/components/StoreOrderStepBadge'
import A from '@/gf/components/A'
import ButtonLink from '@/gf/components/ButtonLinkOld'
import Layout from '@/gf/components/Layout'
import Link from '@/gf/components/Link'
import Page from '@/gf/components/Page'
import PaymentStatusBadge from '@/gf/components/PaymentStatusBadge'
import Tabs from '@/gf/components/Tabs'
import useConfig from '@/gf/hooks/useConfig'
import usePage from '@/gf/hooks/usePage'
import ConversationM from '@/gf/modules/Conversation'
import FilterSet from '@/gf/modules/FilterSet'
import FormatFilters from '@/gf/modules/FormatFilters'
import InvoiceM from '@/gf/modules/Invoice'
import Money from '@/gf/modules/Money'
import RequestForQuoteContext from '@/gf/modules/RequestForQuote'
import Time from '@/gf/modules/Time'
import UserM from '@/gf/modules/User'
import EqualsFilter from '@/gf/modules/filters/Equals'
import {
  PaymentMethod,
  StoreOrderState,
  useFetchConversationsQuery,
  useOrderQuery,
} from '@/suppliers/_gen/gql'
import pluralize from 'pluralize'
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { titleCase } from 'title-case'
import { Invoice, Tab } from '../../types'
import BrokerWarning from '../components/BrokerWarning'
import Frame from '../components/Frame'
import useGqlClient from '../hooks/useGqlClient'
import useSession from '../hooks/useSession'
import QueryString from '../modules/QueryString'
import StoreOrderM from '../modules/StoreOrder'
import { StoreOrder } from '../types'
import Messages from './Order/Messages'
import StoreOrderDetails from './Order/StoreOrderDetails'
import { OrderContext } from './Order/context'
import useFetchStoreOrder from './Order/useFetchStoreOrder'

const FinancedInvoiceButton = ({
  financedInvoices,
  storeOrderState,
  storeOrderShortId,
  adminUrl,
}: {
  financedInvoices: Pick<Invoice, 'isPaid' | 'isCanceled' | 'isFinanced' | 'balanceTransactionId'>[]
  storeOrderState: StoreOrderState
  storeOrderShortId: string
  adminUrl: string
}) =>
  storeOrderState === 'SHIPPED' ||
  storeOrderState === 'READY_FOR_PICKUP' ||
  storeOrderState === 'BUYER_RECEIVED' ||
  storeOrderState === 'REFUNDED' ? (
    <ButtonLink
      primary={false}
      href={`${adminUrl}/invoices${QueryString.invoicesStoreOrderId(storeOrderShortId)}`}
    >
      {`${InvoiceM.isPaymentRequiredForInvoices(financedInvoices) ? 'Pay' : 'View'} ${pluralize(
        'Invoice',
        financedInvoices.length
      )}`}
    </ButtonLink>
  ) : (
    <div className="flex justify-center items-center px-4 py-2 rounded-md shadow-sm border text-sm text-gray-400 border-gray-300 bg-gray-100">
      Invoice available after shipped
    </div>
  )

const NonFinancedInvoiceButton = ({
  nonFinancedInvoices,
  storeOrder,
  adminUrl,
}: {
  nonFinancedInvoices: Invoice[]
  storeOrder: Pick<StoreOrder, 'shortId' | 'state'>
  adminUrl: string
}) =>
  StoreOrderM.isApprovedBySupplier(storeOrder) ? (
    <ButtonLink
      primary={false}
      href={`${adminUrl}/invoices${QueryString.invoicesStoreOrderId(storeOrder.shortId)}`}
    >
      View {pluralize('Receipt', nonFinancedInvoices.length)}
    </ButtonLink>
  ) : null

const Order = () => {
  const { storeOrderId } = useParams<{ storeOrderId: string }>() as { storeOrderId: string }
  const [page, setPage] = usePage()
  const navigate = useNavigate()
  const { balanceDashboardBaseUrl, adminUrl, stripeDashboardBaseUrl } = useConfig()
  const { user, store } = useSession()
  const { pathname } = useLocation()
  const client = useGqlClient()
  const { data, refetch: refetchNew } = useOrderQuery({ variables: { storeOrderId }, client })

  const { data: dataOld, refetch: refetchOld } = useFetchStoreOrder({
    variables: { id: storeOrderId },
  })

  const storeOrder = data?.storeOrder
  const storeOrderOld = dataOld?.storeOrder

  const refetch = () => Promise.all([refetchOld(), refetchNew()])

  const {
    data: conversationsData,
    error: conversationsError,
    refetch: conversationsRefetch,
  } = useFetchConversationsQuery({
    client,
    variables: {
      filters: FormatFilters.andFilters(
        FilterSet.toApiFilters([
          EqualsFilter.build({ fieldId: 'storeOrderId', text: storeOrderId }),
        ])
      ),
      page,
      pageSize: ConversationM.PAGE_SIZE,
      vendorId: null,
      vendorUserId: null,
    },
    pollInterval: ConversationM.CONVERSATION_POLL_INTERVAL,
  })

  const tabNameDetails = 'Details'
  const tabNameMessages = 'Messages'

  const tabs: Tab[] = [
    { name: tabNameDetails },
    {
      name: tabNameMessages,
      showNotification: conversationsData?.fetchConversations.conversations?.some(
        (c) => c.unreadMessages
      ),
    },
  ]

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'Orders', href: '/orders' },
      { name: 'Order details', href: `/orders/${storeOrderId}` },
    ],
  }

  // Return a loading page when the storeOrder is undefined
  if (storeOrder === undefined || storeOrderOld === undefined) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Loading..." />
      </Frame>
    )
  }

  // Not found or not authorized
  // TODO: move auth check to backend
  if (
    !storeOrder ||
    !storeOrderOld ||
    (!UserM.isAdmin(user) && storeOrderOld.store.id !== store.id)
  ) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p>Cannot find that order.</p>

              <p>
                <Link.T to="/orders">Back to all orders</Link.T>
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      </Frame>
    )
  }

  // Reused variables
  const refundableAmount = (() => {
    const subtotal = StoreOrderM.subtotal(storeOrderOld)

    const totalForConvenienceFee = [
      subtotal,
      storeOrderOld.shippingAmount,
      storeOrderOld.salesTax,
    ].reduce(Money.add)

    const convenienceFee = Money.mult(totalForConvenienceFee, storeOrderOld.convenienceFeeRate)

    const paidAdditionalChargesPrice = storeOrderOld.additionalCharges
      .filter((ac) => ac.state === 'charged')
      .map((ac) => ac.price)
      .reduce(Money.add, Money.fromDecimal(0, 'USD'))

    const paidTotal = Money.add(
      StoreOrderM.isUnapprovedBySupplier(storeOrderOld)
        ? Money.fromDecimal(0, 'USD')
        : [subtotal, storeOrderOld.shippingAmount, storeOrderOld.salesTax, convenienceFee].reduce(
            Money.add
          ),
      paidAdditionalChargesPrice
    )

    const result = Money.sub(paidTotal, StoreOrderM.totalRefunded(storeOrderOld))
    const paidAddlCharges = StoreOrderM.paidAdditionalChargesPrice(storeOrderOld)
    return storeOrderOld.features.txns ? result : Money.sub(result, paidAddlCharges)
  })()

  const vendorAccountNumber =
    storeOrderOld?.vendor?.accountNumbers && storeOrderOld.vendor.accountNumbers.length > 0
      ? ` (account number: ${storeOrderOld.vendor.accountNumbers.join(', ')})`
      : ''

  // A net-terms order's Stripe invoice (GEA-3534) — the admin "View in Stripe" target,
  // parity of "View in Balance". Created at full ship, so it's absent until then.
  const stripeNetTermsInvoiceId = storeOrderOld.invoices?.find(
    (invoice) => invoice.stripeInvoiceId
  )?.stripeInvoiceId

  const refundable =
    storeOrderOld.paymentMethod !== PaymentMethod.Direct &&
    StoreOrderM.isApprovedBySupplier(storeOrderOld) &&
    !Money.isZero(refundableAmount)

  // Refunds on a Stripe-Connect vendor's orders are Gearflow's to issue, not the seller's:
  // this app makes no Stripe call, so a refund from here would move no money (GEA-5170).
  const showRefund = refundable && !storeOrderOld.stripePayments

  // Say so, rather than leaving a seller hunting for a button that will never appear.
  const showRefundHandledByGearflow = refundable && storeOrderOld.stripePayments

  return (
    <OrderContext.Provider value={{ storeOrderId, storeOrder, storeOrderOld, refetch }}>
      <Frame breadcrumbs={breadcrumbs}>
        <Page
          title={`Order ${storeOrderOld.shortId}`}
          yellowAlert={
            storeOrderOld?.order?.requestForQuote &&
            RequestForQuoteContext.isUrgent(storeOrderOld.order.requestForQuote)
              ? `The customer indicated that this order is urgent and is seeking ${
                  storeOrderOld.order.requestForQuote.shippingOption &&
                  titleCase(storeOrderOld.order.requestForQuote.shippingOption)
                } shipping`
              : undefined
          }
          titleMetadata={
            <div className="flex gap-2">
              <StoreOrderStepBadge step={storeOrderOld.step} />
              <PaymentStatusBadge paymentStatus={storeOrderOld.paymentStatus} />
              {storeOrderOld.quickOrder && (
                <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-sm">
                  Order now
                </div>
              )}
            </div>
          }
          titleRightContent={
            <div className="flex flex-horizontal justify-end gap-x-2 text-sm text-gray-500">
              {showRefund && <Link.S to={`/orders/${storeOrderId}/refund`}>Refund</Link.S>}

              {showRefundHandledByGearflow && (
                <span className="self-center text-gray-500">
                  Gearflow issues refunds for this order
                </span>
              )}

              {storeOrderOld.paymentMethod === PaymentMethod.Direct
                ? // Pay on account
                  storeOrderOld.invoices &&
                  storeOrderOld.invoices.length > 0 &&
                  !InvoiceM.isBalanceInvoice(storeOrderOld.invoices[0]) &&
                  storeOrderOld.invoices[0]?.link && (
                    <ButtonLink
                      primary={false}
                      href={storeOrderOld.invoices[0].link}
                      target="_blank"
                    >
                      Download Invoice
                    </ButtonLink>
                  )
                : // Not pay on account (Admin-only view Invoice)
                  UserM.isAdmin(user) && (
                    <>
                      {storeOrderOld.invoices &&
                        InvoiceM.getFinanced(storeOrderOld.invoices).length > 0 && (
                          <FinancedInvoiceButton
                            financedInvoices={InvoiceM.getFinanced(storeOrderOld.invoices)}
                            storeOrderState={storeOrderOld.state}
                            storeOrderShortId={storeOrderOld.shortId}
                            adminUrl={adminUrl}
                          />
                        )}

                      {storeOrderOld.invoices &&
                        InvoiceM.getNotFinanced(storeOrderOld.invoices).length > 0 && (
                          <NonFinancedInvoiceButton
                            nonFinancedInvoices={InvoiceM.getNotFinanced(storeOrderOld.invoices)}
                            storeOrder={storeOrderOld}
                            adminUrl={adminUrl}
                          />
                        )}
                    </>
                  )}

              {/* Only allow PO to be downloaded after store order is approved (moves into processing state) */}
              {StoreOrderM.isApprovedBySupplier(storeOrderOld) && (
                <ButtonLink primary={false} href={`/pdf/download/po/${storeOrderOld.id}`}>
                  Download PO
                </ButtonLink>
              )}

              {/* Only allow Packing Slip to be downloaded after store order is approved (moves into processing state) */}
              {StoreOrderM.isApprovedBySupplier(storeOrderOld) && (
                <ButtonLink primary={false} href={`/pdf/download/packing_slip/${storeOrderOld.id}`}>
                  Download Packing Slip
                </ButtonLink>
              )}

              {/* Only allow Quote to be downloaded before store order is approved (before processing state) */}
              {StoreOrderM.isUnapprovedBySupplier(storeOrderOld) && (
                <A.S href={`/pdf/download/quote/${storeOrderOld.id}`}>Download Quote</A.S>
              )}
            </div>
          }
          subtitle={
            <div className="mt-0.5 flex flex-row items-center gap-x-8">
              <div>
                {UserM.isAdmin(user) && (
                  <div className="text-gray-500 text-sm">
                    <p>Store: {storeOrderOld.store.name}</p>
                  </div>
                )}

                <p className="text-sm text-gray-500">
                  Received: {Time.toString(storeOrderOld.insertedAt)}
                </p>

                <p className="text-sm text-gray-500">
                  Updated: {Time.toString(storeOrderOld.updatedAt)}
                </p>

                {UserM.isAdmin(user) ? (
                  <p className="text-sm text-gray-500">
                    {storeOrderOld.paymentMethod === PaymentMethod.Balance &&
                    storeOrderOld.balanceTransaction ? (
                      <a
                        href={`${balanceDashboardBaseUrl}/transactions/${storeOrderOld.balanceTransaction.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View in Balance
                      </a>
                    ) : (
                      <>
                        {storeOrderOld.paymentMethod === PaymentMethod.Direct ? (
                          <>Direct Pay {vendorAccountNumber}</>
                        ) : storeOrderOld.paymentMethod === PaymentMethod.Stripe ? (
                          <a
                            href={`${stripeDashboardBaseUrl}/payments/${storeOrderOld.stripePaymentIntentId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View in Stripe
                          </a>
                        ) : storeOrderOld.paymentMethod === PaymentMethod.Invoice &&
                          stripeNetTermsInvoiceId ? (
                          <a
                            href={`${stripeDashboardBaseUrl}/invoices/${stripeNetTermsInvoiceId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View invoice in Stripe
                          </a>
                        ) : storeOrderOld.paymentMethod === PaymentMethod.Invoice ? (
                          <>Net terms (invoice pending)</>
                        ) : (
                          <>No Balance transaction</>
                        )}
                      </>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">
                    {storeOrderOld.paymentMethod !== PaymentMethod.Balance &&
                      (storeOrderOld.paymentMethod === PaymentMethod.Direct ? (
                        <>Direct Pay {vendorAccountNumber}</>
                      ) : storeOrderOld.paymentMethod === PaymentMethod.Invoice ? (
                        <>Gearflow net terms</>
                      ) : storeOrderOld.paymentMethod === PaymentMethod.Stripe ? (
                        <>Paid via Stripe</>
                      ) : (
                        <>No Balance transaction</>
                      ))}
                  </p>
                )}
              </div>

              {storeOrderOld.store.id === store.id &&
                storeOrderOld.order.requestForQuote?.brokeredByRequestForQuotes &&
                storeOrderOld.order.requestForQuote.brokeredByRequestForQuotes.length > 0 && (
                  <BrokerWarning.Request
                    brokerRfqIds={storeOrderOld.order.requestForQuote.brokeredByRequestForQuotes?.map(
                      ({ id }) => id
                    )}
                  />
                )}
            </div>
          }
        >
          <Tabs
            className="mt-4"
            currentTabName={pathname.includes('/messages') ? tabNameMessages : tabNameDetails}
            onTabSelect={(tabName) => {
              if (tabName === tabNameDetails) navigate(`/orders/${storeOrderId}`)
              if (tabName === tabNameMessages) navigate(`/orders/${storeOrderId}/messages`)
            }}
            tabs={tabs}
          />

          <Routes>
            <Route path="*" element={<StoreOrderDetails />} />

            {['messages', 'messages/:conversationId'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <Messages
                    conversationsData={conversationsData}
                    conversationsError={conversationsError}
                    conversationsRefetch={conversationsRefetch}
                    page={page}
                    setPage={setPage}
                  />
                }
              />
            ))}
          </Routes>
        </Page>
      </Frame>
    </OrderContext.Provider>
  )
}

export default Order
